// BRK-S170 — the eligibility attestations, item by item.
//
// ── WHAT CHANGED ────────────────────────────────────────────────────────────
//
// The resolved configuration has always carried `eligibility: string[]` — the
// statements a broker confirms on behalf of the principal — and the step
// rendered them as a numbered list under ONE "I confirm all of the above" box.
// Nothing was sent: the confirmation was a gate on the Continue button and
// nowhere else, so what the broker attested to existed only in this browser
// until it did not.
//
// Now each statement is its own required checkbox, and the create carries what
// was confirmed. The statements are the CONFIGURATION's, echoed VERBATIM —
// re-wording a statement a broker attested to is the one thing this module
// must never do, which is why `text` is copied through rather than looked up
// from anything.
//
// ── THE INDEX IS THE IDENTITY ───────────────────────────────────────────────
//
// An item is addressed by its POSITION in the configuration's list, because
// position is all the contract gives it: `eligibility` is a bare `string[]`
// with no ids. That is also how the server names the one it refuses
// (`ELIGIBILITY_NOT_CONFIRMED` carries `details.index`), so the same number
// anchors the refusal to the checkbox that produced it.
//
// It follows that a configuration whose statements are REORDERED between a
// draft's start and its filing describes a different attestation. That is
// upstream's to prevent — an order's configuration is snapshotted at create —
// and it is recorded here because an index-keyed identity invites the
// assumption that indices are stable in general. They are not; they are stable
// within one order.
//
// Pure, and in `lib/` like `bondWizard.ts`, so the shape on the wire and the
// reading of a refusal are unit-tested without mounting a wizard (NN #1).
import { ApiError } from "@/lib/api";

/**
 * One confirmed statement, as the create body carries it.
 *
 * `confirmed` is typed `true` rather than `boolean` on purpose: an UNCONFIRMED
 * item is never sent — the step cannot be left until every box is ticked — so a
 * `confirmed: false` entry would be a shape this client has no way to produce
 * and the server has no way to interpret. Making it unrepresentable is cheaper
 * than documenting it.
 */
export interface BondEligibilityItem {
  index: number;
  text: string;
  confirmed: true;
}

/** What the broker attested to, and when. */
export interface BondEligibilityBlock {
  /** ISO-8601, minted at the moment the order is created — not at the moment a
   *  box was ticked. The attestation takes effect when it is filed. */
  attestedAt: string;
  items: BondEligibilityItem[];
}

/**
 * The block for a configuration's statements, or **null** when there is
 * nothing to attest to.
 *
 * Null rather than `{attestedAt, items: []}`: a subproduct that asks for no
 * confirmations produced no attestation, and an empty list timestamped to the
 * second is a record of an event that did not happen.
 *
 * Every statement must be confirmed. A caller that has not collected them all
 * gets null — belt and braces beside the Continue gate, so a partial
 * attestation cannot be filed by a code path that forgot to check.
 */
export function bondEligibilityBlock(
  statements: readonly string[],
  confirmed: ReadonlySet<number>,
  attestedAt: string,
): BondEligibilityBlock | null {
  if (statements.length === 0) return null;
  if (!allEligibilityConfirmed(statements, confirmed)) return null;
  return {
    attestedAt,
    items: statements.map((text, index) => ({ index, text, confirmed: true })),
  };
}

/** Whether every statement has been ticked. The Continue gate, and the same
 *  rule the block builder applies — one definition, so the button and the body
 *  cannot disagree about what "confirmed" means. */
export function allEligibilityConfirmed(
  statements: readonly string[],
  confirmed: ReadonlySet<number>,
): boolean {
  return statements.every((_statement, index) => confirmed.has(index));
}

/**
 * The server's refusal to accept an attestation, anchored to the item it names.
 *
 * `ELIGIBILITY_NOT_CONFIRMED` (422) carries `details.index` — WHICH statement
 * it would not take. Read defensively, exactly as `serverFieldErrors.ts` reads
 * every other code: a body that is not this shape returns null and the caller
 * falls back to the step-level message, because a refusal reported in the wrong
 * place is worse than one reported generically.
 *
 * An index outside the rendered list is also null. There is no checkbox to
 * anchor to, and putting statement 7's refusal on statement 1 tells the broker
 * to re-read the wrong sentence.
 */
export const ELIGIBILITY_NOT_CONFIRMED = "ELIGIBILITY_NOT_CONFIRMED";

export function eligibilityRefusalIndex(
  error: unknown,
  itemCount: number,
): number | null {
  if (!(error instanceof ApiError)) return null;
  const body = error.body;
  if (typeof body !== "object" || body === null) return null;
  const details = (body as Record<string, unknown>).details;
  if (typeof details !== "object" || details === null) return null;
  const record = details as Record<string, unknown>;
  if (record.code !== ELIGIBILITY_NOT_CONFIRMED) return null;
  const index = record.index;
  if (typeof index !== "number" || !Number.isInteger(index)) return null;
  return index >= 0 && index < itemCount ? index : null;
}

/**
 * Whether a refusal is the eligibility one at all, whatever statement (or none)
 * it names (BRK-S320). There is one control now, so a refusal with no usable
 * `index` still belongs on it rather than at step level only.
 */
export function isEligibilityRefusal(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  const body = error.body;
  if (typeof body !== "object" || body === null) return false;
  const details = (body as Record<string, unknown>).details;
  return (
    typeof details === "object" &&
    details !== null &&
    (details as Record<string, unknown>).code === ELIGIBILITY_NOT_CONFIRMED
  );
}
